import { IAgencyFormData } from "agency/model"
import { initializeAPICredentials, resetAPICredentials } from "app/tools/api"
import { dateAdd } from "app/util/date/tools/common"
import { AuthAPI, UserAPI } from "generated-api"
import {
  BAD_CREDENTIAL_ERROR_KEY,
  IAgencyChangeSession,
  INVALID_CREDENTIALS,
  IPasswordChange,
  IUser,
  IUserImpersonateSession,
  IUserImpersonator,
  IUserInfo,
  IUserNewImpersonatorParams,
  IUserRepository,
  IUserRole,
  IUserSignupFormData,
  NO_RIGHT_ERROR_KEY,
  ROLE_NOT_FOUND_ERROR,
  UNKNOWN_ERROR_KEY,
  USER_ALREADY_EXISTS_ERROR,
  USER_ROLE_ALREADY_EXISTS_ERROR
} from "./model"
import { checkIfSessionIsOrWillExpired } from "./tools/common"

export const userRPO: IUserRepository = {
  login(email: string, password: string): Promise<IUserInfo> {
    return new Promise<IUserInfo>((resolve, reject) => {
      AuthAPI.postApiAuthLogin({ email, password })
        .then(async response => {
          initializeAPICredentials(response.session_id)
          const isAdmin = response.permissions.includes("admin0permissions")
          const userInstance = await this.getUserById(response.user_id, isAdmin)
          resolve({
            user: userInstance,
            permissions: response.permissions,
            token: response.session_id,
            tokenExpirationDateTime: dateAdd(new Date(), response.ttl, "seconds"),
            picture: ""
          })
        })
        .catch(error => {
          if (error.status === 401) {
            return reject(BAD_CREDENTIAL_ERROR_KEY)
          } else if (error === "Unauthorized") {
            return reject(NO_RIGHT_ERROR_KEY)
          }
          console.error(error)
          reject(UNKNOWN_ERROR_KEY)
        })
    })
  },
  getUserInfoFromToken(token: string): Promise<IUserInfo | undefined> {
    return new Promise<IUserInfo | undefined>(resolve => {
      initializeAPICredentials(token)
      AuthAPI.getApiAuthStatus()
        .then(async statusResponse => {
          const isAdmin = statusResponse.permissions ? statusResponse.permissions.includes("admin0permissions") : false
          const userInstance = await this.getUserById(statusResponse.user_id, isAdmin)
          resolve({
            user: userInstance,
            permissions: statusResponse.permissions || [],
            token,
            tokenExpirationDateTime: dateAdd(new Date(), (statusResponse as any).ttl || 7200, "seconds"),
            picture: ""
          })
        })
        .catch(error => {
          resetAPICredentials()
          console.error("unable to get user from token in local storage", error)
          resolve(undefined)
        })
    })
  },
  logout(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      AuthAPI.getApiAuthLogout()
        .then(() => {
          resetAPICredentials()
          resolve()
        })
        .catch(error => {
          console.error(error, error)
          reject("Unexpected network/server error")
        })
    })
  },
  renewSession(email: string): Promise<IUserInfo> {
    return new Promise<IUserInfo>((resolve, reject) => {
      AuthAPI.postApiAuthRenew({ email })
        .then(async response => {
          initializeAPICredentials(response.session_id)
          const isAdmin = response.permissions.includes("admin0permissions")
          const userInstance = await this.getUserById(response.user_id, isAdmin)
          resolve({
            user: userInstance,
            permissions: response.permissions,
            token: response.session_id,
            tokenExpirationDateTime: dateAdd(new Date(), response.ttl, "seconds"),
            picture: ""
          })
        })
        .catch(error => {
          if (error.status === 401) {
            return reject(BAD_CREDENTIAL_ERROR_KEY)
          } else if (error === "Unauthorized") {
            return reject(NO_RIGHT_ERROR_KEY)
          }
          console.error(error)
          reject(UNKNOWN_ERROR_KEY)
        })
    })
  },
  getUserById(userId: number, isRequesterAdmin: boolean): Promise<IUser> {
    return new Promise<IUser>((resolve, reject) => {
      // On the server, users having "admin0permissions" permission can only request endpoints that starts with "api/admin/".
      // An admin using a "non admin" endpoints could broke a lot of things in the data.
      // So we protect normal users endpoints by denying routes to the admin
      // This endpoint logic is used by normal and admin users, so we created 2 routes for the same logic
      // One for normal users, and another one for admin
      const method = isRequesterAdmin ? UserAPI.getApiAdminUser : UserAPI.getApiUser
      method(userId)
        .then(response => {
          if (!response) {
            return reject("User not found")
          }
          if (typeof response !== "object" || !response.hasOwnProperty("id")) {
            return reject("Unable to get the user")
          }
          resolve(response)
        })
        .catch(error => {
          console.error(error)
          if (error.status === 401) {
            reject("Unauthorized")
          } else {
            reject("Unexpected network/server error")
          }
        })
    })
  },
  getAgencyUsers(agencyId: number): Promise<IUser[]> {
    checkIfSessionIsOrWillExpired()
    return new Promise<IUser[]>((resolve, reject) => {
      UserAPI.getApiUserList(
        [], // role
        undefined, // offset
        undefined, // limit
        undefined, // search
        undefined, // match
        undefined, // orderby
        undefined, // comparator
        undefined, // status
        agencyId
      )
        .then(response => {
          resolve(response)
        })
        .catch(error => {
          console.error(error, error)
          reject("Unexpected network/server error")
        })
    })
  },
  createUser(email: string, data: Partial<IUser>, clearPassword: string): Promise<IUser> {
    checkIfSessionIsOrWillExpired()
    return new Promise<IUser>((resolve, reject) => {
      UserAPI.postApiUser({
        ...data,
        email,
        password: clearPassword,
        language_id: data.language_id as "en" | "es" | "fr",
        currency_id: data.currency_id as "CAD" | "EUR" | "USD"
      })
        .then(response => {
          resolve(response)
        })
        .catch(error => {
          console.error(error, error)
          if (error?.body?.message.indexOf("email already exists") !== -1) {
            reject(USER_ALREADY_EXISTS_ERROR)
          } else {
            reject("user.create.error.unexpected")
          }
        })
    })
  },
  updateUser(userId: number, partialUser: Partial<IUser>, passwordChange?: IPasswordChange): Promise<IUser> {
    checkIfSessionIsOrWillExpired()
    return new Promise<IUser>(async (resolve, reject) => {
      if (passwordChange) {
        try {
          await userRPO.login(passwordChange.email, passwordChange.currentClearPassword)
        } catch (error) {
          console.error(error)
          return reject(INVALID_CREDENTIALS)
        }
      }
      UserAPI.putApiUser({
        ...partialUser,
        id: userId,
        current_password: passwordChange ? passwordChange.currentClearPassword : undefined,
        password: passwordChange ? passwordChange.newClearPassword : undefined,
        language_id: partialUser.language_id as "en" | "es" | "fr" | undefined,
        currency_id: partialUser.currency_id as "CAD" | "EUR" | "USD" | undefined
      })
        .then(response => resolve(response))
        .catch(error => {
          console.error(error, error)
          if (error?.body?.message.indexOf("email already exists") !== -1) {
            reject(USER_ALREADY_EXISTS_ERROR)
          } else {
            reject("user.update.error.unexpected")
          }
        })
    })
  },
  getUserRoles(userId: number): Promise<IUserRole[]> {
    checkIfSessionIsOrWillExpired()
    return new Promise<IUserRole[]>((resolve, reject) => {
      UserAPI.getApiUserRoleList(userId)
        .then(roles => resolve(roles))
        .catch(error => {
          console.error(error, error)
          reject("Unexpected network/server error")
        })
    })
  },
  addUserRole(userId: number, role: IUserRole): Promise<void> {
    checkIfSessionIsOrWillExpired()
    return new Promise<void>((resolve, reject) => {
      UserAPI.postApiUserRole({ user_id: userId, role })
        .then(() => resolve())
        .catch(error => {
          console.error(error, error)
          if (error.body.message) {
            return reject(USER_ROLE_ALREADY_EXISTS_ERROR)
          }
          reject("Unexpected network/server error")
        })
    })
  },
  deleteUserRole(userId: number, role: IUserRole): Promise<void> {
    checkIfSessionIsOrWillExpired()
    return new Promise<void>((resolve, reject) => {
      UserAPI.deleteApiUserRole(userId, role)
        .then(() => resolve())
        .catch(error => {
          console.error(error, error)
          reject(error.status === 404 ? ROLE_NOT_FOUND_ERROR : "Unexpected network/server error")
        })
    })
  },
  getAvailableImpersonators(userId: number): Promise<IUserImpersonator[]> {
    checkIfSessionIsOrWillExpired()
    return new Promise<IUserImpersonator[]>((resolve, reject) => {
      UserAPI.getApiUserAvailableImpersonatorList(userId)
        .then(userItems => resolve(userItems))
        .catch(error => {
          console.error(error, error)
          reject("Unexpected network/server error")
        })
    })
  },
  getImpersonators(userId: number): Promise<IUserImpersonator[]> {
    checkIfSessionIsOrWillExpired()
    return new Promise<IUserImpersonator[]>((resolve, reject) => {
      UserAPI.getApiUserImpersonatorList(userId)
        .then(userItems => resolve(userItems))
        .catch(error => {
          console.error(error, error)
          reject("Unexpected network/server error")
        })
    })
  },
  impersonateSession(email: string): Promise<IUserImpersonateSession> {
    checkIfSessionIsOrWillExpired()
    return new Promise<IUserImpersonateSession>((resolve, reject) => {
      AuthAPI.postApiAuthImpersonate({ email })
        .then(async impersonateSession => {
          const user = await UserAPI.getApiUser(impersonateSession.user_id)
          initializeAPICredentials(impersonateSession.session_id)
          resolve({
            ...impersonateSession,
            firstname: user?.firstname || "",
            lastname: user?.lastname || ""
          })
        })
        .catch(error => {
          console.error(error, error)
          reject("Unexpected network/server error")
        })
    })
  },
  changeAgencySession(email: string, agencyId: number): Promise<IAgencyChangeSession> {
    checkIfSessionIsOrWillExpired()
    return new Promise<IAgencyChangeSession>((resolve, reject) => {
      AuthAPI.postApiAuthChangeAgency({ agency_id: agencyId, email })
        .then(async changeSession => {
          const user = await UserAPI.getApiUser(changeSession.user_id)
          initializeAPICredentials(changeSession.session_id)
          resolve({
            ...changeSession,
            firstname: user?.firstname || "",
            lastname: user?.lastname || ""
          })
        })
        .catch(error => {
          console.error(error, error)
          reject("Unexpected network/server error")
        })
    })
  },
  addImpersonator(params: IUserNewImpersonatorParams): Promise<IUserImpersonator> {
    checkIfSessionIsOrWillExpired()
    return new Promise<IUserImpersonator>((resolve, reject) => {
      UserAPI.postApiUserImpersonator(params)
        .then(item => resolve(item))
        .catch(error => {
          console.error(error, error)
          reject("Unexpected network/server error")
        })
    })
  },
  updateImpersonator(params: IUserImpersonator): Promise<IUserImpersonator> {
    checkIfSessionIsOrWillExpired()
    return new Promise<IUserImpersonator>((resolve, reject) => {
      UserAPI.putApiUserImpersonator(params)
        .then(item => resolve(item))
        .catch(error => {
          console.error(error, error)
          reject("Unexpected network/server error")
        })
    })
  },
  deleteImpersonator(selfUserId: number, allowedUserId: number): Promise<void> {
    checkIfSessionIsOrWillExpired()
    return new Promise<void>((resolve, reject) => {
      UserAPI.deleteApiUserImpersonator(selfUserId, allowedUserId)
        .then(() => resolve())
        .catch(error => {
          console.error(error, error)
          reject("Unexpected network/server error")
        })
    })
  },
  createAdstandingUser(user: IUserSignupFormData, agency: IAgencyFormData, captcha: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      UserAPI.postApiUserSignup({
        "email": user.email,
        "firstname": user.firstname,
        "lastname": user.lastname,
        "agency_name": agency.name,
        "agency_currency": agency.currency_id,
        "agency_language": agency.language_id,
        "language": user.language_id,
        "phone": user.phone,
        "password": user.password1,
        "country": agency.country,
        "state": agency.state || "",
        "g-recaptcha-response": captcha
      })
        .then(() => resolve())
        .catch(error => {
          console.error(error)
          if (error.body && error.body.message && error.body.message.indexOf("email already exists") !== -1) {
            reject(USER_ALREADY_EXISTS_ERROR)
            return
          }
          reject("Unexpected network/server error")
        })
    })
  },
  confirmAdstandingUserEmail(email: string, code: string): Promise<IUser> {
    return new Promise<IUser>((resolve, reject) => {
      UserAPI.getApiUserSignupConfirmEmail(email, code)
        .then(user => resolve(user))
        .catch(error => {
          console.error(error)
          reject("Unexpected network/server error")
        })
    })
  },
  resetPasswordDemand(email: string, captcha: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      UserAPI.postApiUserResetPasswordDemand({
        email,
        "g-recaptcha-response": captcha
      })
        .then(resolve)
        .catch(error => {
          console.error(error)
          reject("Unexpected network/server error")
        })
    })
  },
  resetPassword(email: string, code: string, password: string, captcha: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      UserAPI.postApiUserResetPassword({
        email,
        "g-recaptcha-response": captcha,
        password,
        code
      })
        .then(resolve)
        .catch(error => {
          console.error(error)
          reject("Unexpected network/server error")
        })
    })
  }
}
